import { MESSAGE_CHANNELS, channelIncludedInTier } from "../../../config/messageChannels";
import { useAuth } from "../../../hooks/useAuth";
import { useOnboardingDraft } from "../OnboardingContext";
import styles from "../OnboardingWizard.module.css";
import type { StepProps } from "../OnboardingWizard";

export const ChannelStep = ({ onNext, onBack }: StepProps): JSX.Element => {
  const { draft, update } = useOnboardingDraft();
  const { user } = useAuth();
  const tier = user?.tier ?? null;
  const selected = draft.preferredChannel;
  // A locked tile can't be the submitted answer: a stale draft (or an
  // impersonated view) could carry a slug outside the tier, so Next
  // re-checks the selection rather than trusting it.
  const selectedIncluded = selected !== undefined && channelIncludedInTier(tier, selected);

  return (
    <section className={styles.card}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>Where do you want to chat with your agent?</h2>
        <p className={styles.stepHint}>
          Pick the messaging channel you'll use most. You can connect more channels later from your
          dashboard.
        </p>
      </div>

      <div className={styles.field}>
        <div className={`${styles.optionGrid} ${styles.optionGridUniform}`}>
          {MESSAGE_CHANNELS.map((channel) => {
            const included = channelIncludedInTier(tier, channel.slug);
            const isSelected = selected === channel.slug;
            return (
              <button
                key={channel.slug}
                type="button"
                className={[
                  styles.optionTile,
                  isSelected ? styles.optionTileSelected : "",
                  included ? "" : styles.optionTileLocked,
                ].join(" ")}
                onClick={() => update("preferredChannel", channel.slug)}
                disabled={!included}
                aria-pressed={isSelected}
              >
                <span className={styles.optionTitle}>
                  {channel.name}
                  {channel.recommended && included ? (
                    <span className={styles.optionBadge}>Recommended</span>
                  ) : null}
                </span>
                <span className={styles.optionMeta}>{channel.description}</span>
                {!included ? (
                  <span className={styles.optionLockedNote}>Available on higher plans</span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {selected === "whatsapp" ? (
        <div className={`${styles.calloutBanner} ${styles.calloutBannerWarning}`}>
          <strong>WhatsApp needs its own phone number.</strong> Your agent can't run on your personal
          number, because you'll be messaging it from your own WhatsApp. Most customers use a spare
          SIM or cheap eSIM for the agent. Have that number ready before setup.
        </div>
      ) : null}

      <div className={styles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onBack}>Back</button>
        <button type="button" className={styles.primaryBtn} onClick={onNext} disabled={!selectedIncluded}>Next</button>
      </div>
    </section>
  );
};
